import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useForm, Controller } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Container, Icon } from "@hegias/ui-components";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";

import useLocalStorage from "hooks/useLocalStorage";
import { renderInput } from "components/FormComponents/FormComponents";
import { EMAIL } from "../../common/regex";
import useAuth from "../../contexts/Auth/useAuth";
import { PROJECTS, CREATE_ORGANIZATION } from "../../common/routes.constants";
import {
	FormErrorMessage,
	ErrorMessage,
	Link,
	LanguageSwitcher,
	Containers,
} from "../../components";

const Login = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const { search, state } = useLocation<{
		destinationPath?: string;
	}>();

	const [isPreselectedPlan] = useLocalStorage("plan-preselect", null);
	const [loginAttepmts, setLoginAttpemts] = useLocalStorage(
		"login-attepmts",
		0,
	);

	const [errorMessage, setErrorMessage] = useState("");
	const [showCaptcha, setShowCaptcha] = useState<boolean>(false);

	const { login, loading, authenticated, logout } = useAuth();
	const { control, handleSubmit, errors } = useForm();

	const redirectPath: string = React.useMemo(() => {
		if (isPreselectedPlan) {
			return `/${CREATE_ORGANIZATION}`;
		}

		return state?.destinationPath ? state.destinationPath : `/${PROJECTS}`;
	}, [state?.destinationPath, isPreselectedPlan]);

	const onSubmit = async (data: { email: string; password: string }) => {
		setErrorMessage("");
		const { email, password } = data;

		try {
			await login(email, password);

			history.push({
				pathname: redirectPath,
				search,
			});
		} catch (error) {
			setLoginAttpemts(loginAttepmts + 1);
			setErrorMessage(error.message);
		}
	};

	useEffect(() => {
		// Show captcha if user fail 2 or more login attempts.
		if (loginAttepmts >= 2) {
			setShowCaptcha(true);
		}

		logout();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loginAttepmts]);

	if (authenticated) {
		history.push({
			pathname: redirectPath,
			search,
		});
	}

	return (
		<Container image style={{ height: "100vh" }}>
			<LanguageSwitcher
				style={{ position: "absolute", top: "20px", right: "20px" }}
			/>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					width: "250px",
					height: "60%",
				}}
			>
				<form
					style={{
						display: "grid",
						width: "100%",
						gridTemplateColumns: "1fr",
						justifyItems: "center",
						rowGap: "8px",
					}}
					autoComplete="on"
				>
					<Icon name="LogoVerticalSloganIcon" size="100px" />
					<ErrorMessage message={t(errorMessage)} />

					<Controller
						control={control}
						render={renderInput({
							placeholder: t("app.global.placeholder.email"),
							autoComplete: "email",
						})}
						rules={{
							required: t(
								"app.form.error.requiredField",
							) as string,
							pattern: {
								value: EMAIL,
								message: t(
									"app.form.error.invalidEmailFormat",
								) as string,
							},
						}}
						name="email"
						style={{ width: "250px" }}
					/>
					<FormErrorMessage errors={errors} name="email" />
					<Controller
						control={control}
						render={renderInput({
							placeholder: t("app.global.placeholder.password"),
							autoComplete: "current-password",
							type: "password",
						})}
						rules={{
							required: t(
								"app.form.error.requiredField",
							) as string,
						}}
						name="password"
						style={{ width: "250px" }}
					/>
					<FormErrorMessage errors={errors} name="password" />
					{showCaptcha && process.env.REACT_APP_GOOGLE_RECAPTCHA && (
						<StyledReCaptcha>
							<ReCAPTCHA
								sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA}
								onChange={() => {
									setShowCaptcha(false);
									// User can attempt 2 more times no.
									setLoginAttpemts(0);
								}}
							/>
						</StyledReCaptcha>
					)}
					<Button
						loading={loading}
						disabled={loading || showCaptcha}
						style={{ width: "250px" }}
						onClick={handleSubmit(onSubmit)}
					>
						{t("app.Login.btn.login")}
					</Button>
					<Containers.ButtonGroup
						type="vertical"
						style={{
							justifySelf: "baseline",
							width: "auto",
						}}
					>
						<Link to="/register">
							{t("app.Login.link.toRegister")}
						</Link>
						<Link to="/forgot-password">
							{t("app.Login.link.toForgotPassword")}
						</Link>
					</Containers.ButtonGroup>
				</form>
			</div>
		</Container>
	);
};

const StyledReCaptcha = styled.div`
	width: 250px;
	max-width: 250px;
	margin-top: 5px;
	margin-left: -50px;
`;

export default Login;
